/**
 * Лёгкий pub/sub для ненавязчивого промпта апгрейда тарифа (CTHS-21, AC2).
 *
 * Axios-интерцептор ({@link ./AxiosSupport}) при ответе 402 (исчерпана квота / достигнут
 * лимит ресурса) или 403-с-маркером-апгрейда (premium-аналитика) публикует сюда событие,
 * а смонтированный в дереве баннер {@code UpgradePrompt} на него подписан и показывает
 * понятное объяснение и путь апгрейда. Так обработка 402/403 остаётся глобальной и не
 * дублируется на каждой странице, при этом исходный промис запроса всё равно reject-ится —
 * вызывающий код продолжает обрабатывать ошибку как обычно.
 */
export interface UpgradePromptEvent {
    /** Заголовок (из тела ошибки backend, поле {@code error}); fallback — дефолтный текст. */
    title: string;
    /** Пояснение с подсказкой апгрейда (поле {@code description}); может отсутствовать. */
    description?: string;
}

type Listener = (event: UpgradePromptEvent) => void;

const listeners = new Set<Listener>();

/** Подписаться на промпты апгрейда. Возвращает функцию отписки. */
export const onUpgradePrompt = (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/**
 * Счётчик смонтированных экранов, которые объясняют premium-гейт сами — inline-панелью
 * {@code PremiumUpsell} на месте недоступных данных (R17).
 *
 * Пока такой экран на виду, глобальный баннер не поднимаем: иначе на один и тот же отказ
 * пользователь получает два сообщения — всплывающий баннер сверху и объяснение в теле
 * страницы. Счётчик (а не флаг) нужен потому, что на одном экране может быть несколько
 * premium-виджетов, и размонтирование одного не должно возвращать баннер.
 */
let inlineSurfaces = 0;

/**
 * Сообщить, что смонтирован экран с собственным объяснением premium-гейта.
 * Возвращает функцию снятия — вызывать при размонтировании (idempotent).
 */
export const registerInlineUpgradeSurface = (): (() => void) => {
    inlineSurfaces += 1;
    let released = false;
    return () => {
        if (!released) {
            released = true;
            inlineSurfaces = Math.max(0, inlineSurfaces - 1);
        }
    };
};

/** Есть ли на экране собственное объяснение premium-гейта. */
export const hasInlineUpgradeSurface = (): boolean => inlineSurfaces > 0;

/**
 * Подписчики на «убери баннер»: inline-панель, появившаяся уже после того, как тост
 * поднялся, снимает его сама. Порядок событий бывает и такой — ответ 403 приходит раньше,
 * чем экран успевает смонтировать объяснение (Q19).
 */
const dismissListeners = new Set<() => void>();

/** Подписаться на снятие баннера апгрейда. Возвращает функцию отписки. */
export const onUpgradePromptDismiss = (listener: () => void): (() => void) => {
    dismissListeners.add(listener);
    return () => {
        dismissListeners.delete(listener);
    };
};

/** Снять показанный баннер апгрейда: объяснение уже есть на самой странице. */
export const dismissUpgradePrompt = (): void => {
    dismissListeners.forEach((listener) => listener());
};

/**
 * Опубликовать промпт апгрейда всем подписчикам. Если экран объясняет гейт сам
 * ({@link registerInlineUpgradeSurface}), событие не публикуется.
 */
export const emitUpgradePrompt = (event: UpgradePromptEvent): void => {
    if (hasInlineUpgradeSurface()) {
        return;
    }
    listeners.forEach((listener) => listener(event));
};

/** Тело ошибки от backend GlobalExceptionHandler: {@code {error, code, description}}. */
export interface ApiErrorBody {
    error?: string;
    code?: number;
    description?: string;
}

/**
 * Маркер, по которому 403 отличается от обычного «forbidden»/IDOR и трактуется как требующий
 * апгрейда (premium-аналитика, CTHS-20). Совпадает с UPGRADE_HINT в QuotaEnforcementServiceImpl.
 */
export const UPGRADE_HINT_MARKER = 'Обновите тариф';

/**
 * Чистое решение: показывать ли промпт апгрейда по ответу об ошибке (CTHS-21, FR-3/FR-4).
 * 402 (квота/лимит ресурса) — всегда; 403 — только при наличии подсказки апгрейда в описании,
 * чтобы не реагировать на обычные отказы доступа (admin-эндпоинты, чужие ресурсы, IDOR).
 */
export const shouldPromptUpgrade = (status: number | undefined, body: ApiErrorBody | undefined): boolean => {
    if (status === 402) {
        return true;
    }
    return status === 403
        && typeof body?.description === 'string'
        && body.description.includes(UPGRADE_HINT_MARKER);
};
